import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getGovernorLiveActions,
  subscribeGovernorLiveActions,
  type GovernorLiveAction,
} from '@/lib/governorEventBus';

import {
  projectForegroundCallCertification,
} from '@/governor/calls-workspace/CallsForegroundCertificationProjection';

type ManualObservationState =
  Readonly<{
    callerHeardReceiver:
      boolean;
    receiverHeardCaller:
      boolean;
    callerSawReceiver:
      boolean;
    receiverSawCaller:
      boolean;
    ringingStopped:
      boolean;
    correctReturnRoute:
      boolean;
  }>;

const initialManualState:
  ManualObservationState =
    Object.freeze({
      callerHeardReceiver: false,
      receiverHeardCaller: false,
      callerSawReceiver: false,
      receiverSawCaller: false,
      ringingStopped: false,
      correctReturnRoute: false,
    });

const observationLabels:
  ReadonlyArray<
    readonly [
      keyof ManualObservationState,
      string,
    ]
  > = [
    [
      'callerHeardReceiver',
      'Caller heard receiver',
    ],
    [
      'receiverHeardCaller',
      'Receiver heard caller',
    ],
    [
      'callerSawReceiver',
      'Caller saw receiver during video test',
    ],
    [
      'receiverSawCaller',
      'Receiver saw caller during video test',
    ],
    [
      'ringingStopped',
      'Receiver stopped ringing after acceptance',
    ],
    [
      'correctReturnRoute',
      'Both devices returned to the correct authenticated surface',
    ],
  ];

export default function CallsForegroundCertificationWorkspace() {
  const [
    liveActions,
    setLiveActions,
  ] =
    useState<readonly GovernorLiveAction[]>(
      () =>
        getGovernorLiveActions(),
    );

  const [
    callSessionId,
    setCallSessionId,
  ] =
    useState('');

  const [
    testMode,
    setTestMode,
  ] =
    useState<
      'voice' | 'video'
    >('voice');

  const [
    manual,
    setManual,
  ] =
    useState<ManualObservationState>(
      initialManualState,
    );

  useEffect(
    () =>
      subscribeGovernorLiveActions(
        actions =>
          setLiveActions(actions),
      ),
    [],
  );

  const projection =
    useMemo(
      () =>
        projectForegroundCallCertification(
          liveActions,
          callSessionId,
        ),
      [
        liveActions,
        callSessionId,
      ],
    );

  const requiredManualKeys:
    readonly (
      keyof ManualObservationState
    )[] =
      testMode === 'video'
        ? [
            'callerHeardReceiver',
            'receiverHeardCaller',
            'callerSawReceiver',
            'receiverSawCaller',
            'ringingStopped',
            'correctReturnRoute',
          ]
        : [
            'callerHeardReceiver',
            'receiverHeardCaller',
            'ringingStopped',
            'correctReturnRoute',
          ];

  const manualComplete =
    requiredManualKeys.every(
      key =>
        manual[key],
    );

  const readyForReview =
    projection.lifecycleComplete &&
    manualComplete;

  return (
    <section className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50 p-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-fuchsia-800">
          Controlled live certification
        </p>

        <h4 className="mt-2 text-2xl font-bold text-slate-950">
          Foreground Two-Device Test
        </h4>

        <p className="mt-2 max-w-4xl text-sm text-slate-700">
          Enter the session ID created by one real foreground call.
          Lifecycle requirements are derived only from correlated LIVE
          Governor events. Manual observations are supplemental and do
          not independently certify Calls.
        </p>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_12rem]">
        <label>
          <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Call-session ID
          </span>

          <input
            value={callSessionId}
            onChange={event =>
              setCallSessionId(
                event.target.value,
              )
            }
            placeholder="Paste the real call_sessions ID"
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
          />
        </label>

        <label>
          <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Test mode
          </span>

          <select
            value={testMode}
            onChange={event => {
              setTestMode(
                event.target
                  .value as
                    | 'voice'
                    | 'video',
              );

              setManual(
                initialManualState,
              );
            }}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
          >
            <option value="voice">
              Voice
            </option>

            <option value="video">
              Video
            </option>
          </select>
        </label>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {projection.requirements.map(
          requirement => (
            <div
              key={
                requirement.requirement
              }
              className={
                'rounded-xl border p-4 ' +
                (
                  requirement.passed
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-amber-200 bg-amber-50'
                )
              }
            >
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
                {
                  requirement.requirement
                }
              </p>

              <p className="mt-2 font-bold text-slate-950">
                {requirement.passed
                  ? 'PASS'
                  : 'WAITING'}
              </p>

              {requirement.stage && (
                <p className="mt-2 text-xs text-slate-600">
                  {requirement.stage}
                </p>
              )}
            </div>
          ),
        )}
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
          Human-observed device checks
        </p>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {observationLabels.map(
            ([key, label]) => {
              const required =
                requiredManualKeys.includes(
                  key,
                );

              return (
                <label
                  key={key}
                  className={
                    'flex items-start gap-3 rounded-xl border p-3 ' +
                    (
                      required
                        ? 'border-slate-300 bg-slate-50'
                        : 'border-slate-200 bg-slate-100 opacity-60'
                    )
                  }
                >
                  <input
                    type="checkbox"
                    disabled={!required}
                    checked={
                      manual[key]
                    }
                    onChange={event =>
                      setManual(
                        current =>
                          Object.freeze({
                            ...current,
                            [key]:
                              event.target
                                .checked,
                          }),
                      )
                    }
                    className="mt-1"
                  />

                  <span className="text-sm text-slate-800">
                    {label}
                  </span>
                </label>
              );
            },
          )}
        </div>
      </div>

      <div
        className={
          'mt-5 rounded-xl border p-4 text-sm ' +
          (
            readyForReview
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : projection.status ===
                  'failed'
                ? 'border-rose-200 bg-rose-50 text-rose-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
          )
        }
      >
        <p className="font-bold">
          {readyForReview
            ? 'Foreground test ready for Governor review'
            : projection.status ===
                'failed'
              ? 'Foreground test failed'
              : 'Foreground test incomplete'}
        </p>

        <p className="mt-1">
          {
            projection.certificationReason
          }
        </p>

        <p className="mt-2">
          Lifecycle evidence:{' '}
          <b>
            {
              projection.passedCount
            }
            /
            {
              projection.requiredCount
            }
          </b>
          {' · '}
          Blocking events:{' '}
          <b>
            {
              projection.blockingFailures
            }
          </b>
        </p>

        <p className="mt-2 font-bold">
          Calls certification remains withheld.
        </p>
      </div>
    </section>
  );
}
